<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';

import { useBreakpointMediaQueries } from '@sg-core/resources/assets/js/composables/use-breakpoint-media-queries';
import { debounce, getBreakpoint, rtlInvertedNumber } from '@sg-core/resources/assets/js/utils';
import type { PhxsgSliderProps as PhxsgCoreSliderProps } from '@sg-core/src/View/Components/Organisms/Slider';
import { mergeOptions } from '@sg-core/src/View/Components/Organisms/Slider/phxsg-slider.vue';

import { PhxsgContainer } from '../../Atoms/Container';
import { PhxsgSlider } from '../../Organisms/Slider';
import { PhxsgSliderProps } from '../Slider/phxsg-slider.vue';

import type { Swiper as SwiperType } from 'swiper';

const props = withDefaults(defineProps<PhxsgCoreSliderProps>(), {
  navigation: true,
  pagination: () => ({ xs: true, md: false }),
});

const swiperInstance = ref<SwiperType | null>(null);
const activeIndex = ref(0);
const tabletBreakpoint = getBreakpoint('md');
const desktopBreakpoint = getBreakpoint('xl');
const disableAnimation = ref(true);

const currentBreakpoint = useBreakpointMediaQueries(['md']);

/**
 * Whether the viewport is at the `md` breakpoint or above, i.e. the
 * large-screen layout where slide transforms and trailing offset apply.
 */
const isLargeLayout = computed(() => {
  return currentBreakpoint.value === 'md';
});

const slidesOffsetAfter = ref(0);

const mergedOptions = computed(() => {
  const defaultOptions: PhxsgCoreSliderProps = {
    spaceBetween: 8,
    speed: 500,
    /**
     * Allow the last slide to park flush on the left edge.
     */
    slidesOffsetAfter: slidesOffsetAfter.value,
    /**
     * On small screens, use centeredSlides + centeredSlidesBounds so that
     * middle slides are centered while first/last slides stay flush with
     * container edges. Disabled at md+ via the breakpoints override.
     */
    centeredSlides: true,
    centeredSlidesBounds: true,

    breakpoints: {
      [tabletBreakpoint]: {
        spaceBetween: 8,
        centeredSlides: false,
        centeredSlidesBounds: false,
      },
      [desktopBreakpoint]: {
        spaceBetween: 24,
        centeredSlides: false,
        centeredSlidesBounds: false,
      },
    },
  };

  return mergeOptions(defaultOptions, props) as PhxsgSliderProps;
});

const updateSlidesOffsetAfter = () => {
  if (!swiperInstance.value) {
    return;
  }

  const swiper = swiperInstance.value;

  if (!isLargeLayout.value) {
    slidesOffsetAfter.value = 0;
  } else {
    const swiperEl = swiper.el;
    const containerWidth = swiperEl.offsetWidth;
    const activeSlideWrapper = swiperEl.querySelector('.swiper-slide-active .phxsg-page-slider__slide-wrapper');
    const activeSlide = swiper.slides[swiper.activeIndex];
    const activeWidth =
      (activeSlideWrapper as HTMLElement | null)?.offsetWidth ?? (activeSlide as HTMLElement | null)?.offsetWidth ?? 0;
    const offset = Math.max(0, containerWidth - activeWidth);

    slidesOffsetAfter.value = offset;
  }

  if (swiper.params.slidesOffsetAfter !== slidesOffsetAfter.value) {
    swiper.params.slidesOffsetAfter = slidesOffsetAfter.value;
    swiper.update();
  }
};

interface SetSlidesStyleOptions {
  updateOffset?: boolean;
}

const setSlidesStyle = ({ updateOffset = true }: SetSlidesStyleOptions = {}) => {
  if (!swiperInstance.value) {
    return;
  }

  const inactiveWidth = parseFloat(
    getComputedStyle(swiperInstance.value.el).getPropertyValue('--this-slider-item-inactive-width'),
  );

  swiperInstance.value.slides.forEach((slide, index) => {
    const slideWrapper = slide.querySelector('.phxsg-page-slider__slide-wrapper') as HTMLElement;

    if (!slideWrapper) {
      return;
    }

    // Reset translate for mobile
    if (!isLargeLayout.value) {
      slideWrapper.style.translate = '';

      return;
    }

    const shouldTranslate = index > activeIndex.value + 1;

    if (shouldTranslate) {
      const gapPerSlide = slide.offsetWidth * (1 - inactiveWidth / 100);
      const multiplier = index - activeIndex.value - 1;
      const translate = multiplier * gapPerSlide * -1;

      slideWrapper.style.translate = `${rtlInvertedNumber(translate)}px`;
    } else {
      slideWrapper.style.translate = '0';
    }
  });

  if (updateOffset) {
    updateSlidesOffsetAfter();
  }
};

/**
 * Lightweight style-only refresh: sync active index and recompute slide
 * CSS transforms without touching Swiper layout. Safe to call during an
 * in-flight transition (no `swiper.update()`).
 */
const refreshStyles = () => {
  if (!swiperInstance.value) {
    return;
  }

  activeIndex.value = swiperInstance.value.realIndex;
  setSlidesStyle({ updateOffset: false });
};

/**
 * Full layout refresh: sync active index and recalculate slide styles and
 * offset. Used for init, resize, and breakpoint changes — contexts where
 * no Swiper transition is in progress.
 */
const refreshLayout = () => {
  if (!swiperInstance.value) {
    return;
  }

  activeIndex.value = swiperInstance.value.realIndex;
  setSlidesStyle();
};

const onSwiper = (swiper: SwiperType) => {
  swiperInstance.value = swiper;

  /**
   * Delay the first layout recalculation until Swiper and the DOM have
   * settled. On the very first render the slide dimensions may not yet
   * be correct, so we wait for two animation frames (one to let Swiper
   * finish its internal layout, one to let the browser paint) before
   * measuring.
   */
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      refreshLayout();

      nextTick(() => {
        disableAnimation.value = false;
      });
    });
  });

  /**
   * During a slide transition only update visual styles (CSS transforms);
   * calling `swiper.update()` here would interrupt the in-flight
   * animation. Layout changes are applied on init, resize, and breakpoint.
   */
  swiper.on('slideChange', refreshStyles);
  swiper.on('breakpoint', refreshLayout);
};

const handleResize = debounce(refreshLayout, 50);

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<template>
  <PhxsgContainer class="phxsg-page-slider-container">
    <PhxsgSlider
      :class="['phxsg-page-slider', { 'phxsg-slider--no-animation': disableAnimation }]"
      v-bind="mergedOptions"
      controls-layout="outer"
      @swiper="onSwiper"
    >
      <slot />
    </PhxsgSlider>
  </PhxsgContainer>
</template>

<style src="./page-slider.css" />
